import {
  column,
  ContentHeader,
  emptyCell,
  GridCellContent,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  groupColumns,
  numericColumn,
  Palette,
  simpleGroupHeader,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  Tooltip,
  useGridTableApi,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { formatToPrice, priceCell, tagCell } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostOptionGroupOfferingStatus } from "src/generated/graphqlTypes";
import { ReloadCostsBanner } from "./components/ReloadCostsBanner";
import {
  OptionsTab_CostOptionGroupRollupFragment,
  OptionsTab_RollupStatsFragment,
  OptionsTabQuery,
  useOptionsTabQuery,
} from "./OptionsTab.generated";
import { deltaPercentageCell, deltaPriceCell } from "./utils";

type OptionsTabProps = { productOfferings: NamedFragment[] };

export function OptionsTab(props: OptionsTabProps) {
  const { productOfferings } = props;
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const api = useGridTableApi<Row>();
  // One row per option group and checkout location across the offerings, so they're all loaded and sorted here
  const query = useOptionsTabQuery({
    variables: { filter: { productOfferings: productOfferingIds } },
    skip: productOfferingIds.isEmpty,
  });
  const columns = useMemo(() => createColumns(productOfferings), [productOfferings]);
  const createRows = useCallback(
    (data: OptionsTabQuery | undefined): GridDataRow<Row>[] => [
      simpleGroupHeader,
      simpleHeader,
      ...(data?.costOptionGroupSummaries ?? []).map((rollup) => ({
        kind: "data" as const,
        // The rows have no ids of their own, so the group and location identify them
        id: `${rollup.globalOptionGroup.id}_${rollup.locationName ?? "none"}`,
        data: rollup,
      })),
    ],
    [],
  );
  return (
    <>
      {/* NOTE: We'll need a different banner for the options costs job */}
      <ReloadCostsBanner productOfferingIds={productOfferingIds} onReload={() => query.refetch()} />
      <ContentHeader
        title="Options"
        description="Review option costs and how they vary across Product Offerings. Costs recalculate automatically whenever a cost-changing edit is saved."
      />
      <GridTableLayout
        emptyFallback="There aren't any option groups to display."
        // Columns with the same name share one Edit Columns switch
        shareColumnVisibility
        tableProps={{
          api,
          query,
          createRows,
          columns,
          sorting: { on: "client", initial: ["name", "ASC"] },
          as: "virtual",
          // Stripes every other visible group, so hiding All Product Offerings doesn't leave two plain groups side by side
          style: { columnStriping: Palette.Gray100 },
        }}
      />
    </>
  );
}

type Rollup = OptionsTab_CostOptionGroupRollupFragment;
type Stats = OptionsTab_RollupStatsFragment;
type Summary = NonNullable<Rollup["offerings"][number]["summary"]>;
type Row = SimpleHeaderAndData<Rollup> | { kind: "groupHeader" };

function createColumns(offerings: NamedFragment[]): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "name",
      name: "Name",
      header: "Name",
      groupHeader: emptyCell,
      canHide: false,
      data: ({ globalOptionGroup }) => globalOptionGroup.name,
      mw: "240px",
      sticky: "left",
    }),
    column<Row>({
      id: "type",
      name: "Type",
      header: "Type",
      groupHeader: emptyCell,
      canHide: true,
      data: ({ globalOptionGroup }) => tagCell("neutral", globalOptionGroup.type.name),
      mw: "140px",
    }),
    column<Row>({
      id: "location",
      name: "Location",
      header: "Location",
      groupHeader: emptyCell,
      canHide: false,
      data: ({ locationName }) => locationName ?? "",
      mw: "140px",
    }),
    column<Row>({
      id: "code",
      name: "Code",
      header: "Code",
      groupHeader: emptyCell,
      canHide: true,
      data: ({ optionCode }) => optionCode ?? "",
      mw: "120px",
    }),
    ...groupColumns<Row>({
      id: "allProductOfferings",
      header: "All Product Offerings",
      columns: [
        ...statsColumns("cost", "Cost", (rollup) => rollup.costs, false),
        ...statsColumns("price", "Price", (rollup) => rollup.prices, true),
      ],
    }),
    ...offerings.flatMap((po) => groupColumns<Row>({ id: po.id, header: po.name, columns: offeringColumns(po) })),
  ];
}

/** An amount's range, average and spread across every offering. */
function statsColumns(
  id: string,
  label: string,
  stats: (rollup: Rollup) => Stats,
  initHidden: boolean,
): GridColumn<Row>[] {
  return [
    numericColumn<Row>({
      id: `${id}Range`,
      name: `${label} Range`,
      header: `${label} Range`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden,
      mw: "200px",
      data: (rollup) => rangeCell(stats(rollup)),
    }),
    numericColumn<Row>({
      id: `average${label}InCents`,
      name: `Avg ${label}`,
      header: `Avg ${label}`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden,
      mw: "120px",
      data: (rollup) => priceCell({ valueInCents: stats(rollup).averageInCents }),
    }),
    numericColumn<Row>({
      id: `${id}DeltaInCents`,
      name: `Δ ${label} $`,
      header: `Δ ${label} $`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      data: (rollup) => deltaPriceCell(stats(rollup).deltaInCents),
    }),
    numericColumn<Row>({
      id: `${id}DeltaPercentage`,
      name: `Δ ${label} %`,
      header: `Δ ${label} %`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      data: (rollup) => deltaPercentageCell(stats(rollup).deltaPercentage),
    }),
  ];
}

function offeringColumns(po: NamedFragment): GridColumn<Row>[] {
  return [
    numericColumn<Row>({
      id: po.id,
      name: "Cost",
      header: "Cost",
      groupHeader: emptyCell,
      canHide: false,
      mw: "200px",
      data: (rollup) => offeringCell(rollup, po),
    }),
    ...deltaColumns(po, "Cost"),
    numericColumn<Row>({
      id: `${po.id}_price`,
      name: "Price Range",
      header: "Price",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "200px",
      data: (rollup) => {
        const summary = getOfferingSummary(rollup, po);
        return summary
          ? { content: priceRange(summary.minPriceInCents, summary.maxPriceInCents), value: summary.minPriceInCents }
          : emptyCell;
      },
    }),
    ...deltaColumns(po, "Price"),
  ];
}

function deltaColumns(po: NamedFragment, amount: "Cost" | "Price"): GridColumn<Row>[] {
  return [
    numericColumn<Row>({
      id: `${po.id}_delta${amount}InCents`,
      name: `Δ ${amount} $`,
      header: `Δ ${amount} $`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      data: (rollup) => {
        const summary = getOfferingSummary(rollup, po);
        return summary ? deltaPriceCell(summary[`delta${amount}InCents`]) : emptyCell;
      },
    }),
    numericColumn<Row>({
      id: `${po.id}_delta${amount}Percentage`,
      name: `Δ ${amount} %`,
      header: `Δ ${amount} %`,
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      data: (rollup) => {
        const summary = getOfferingSummary(rollup, po);
        return summary ? deltaPercentageCell(summary[`delta${amount}Percentage`]) : emptyCell;
      },
    }),
  ];
}

/** The offering's summary on the row, when it's costed there. */
function getOfferingSummary(rollup: Rollup, po: NamedFragment): Summary | undefined {
  return rollup.offerings.find((o) => o.productOffering.id === po.id)?.summary ?? undefined;
}

/** The offering's cost range. N/A when there's none for the location, or a tag when it has it but not the group. */
function offeringCell(rollup: Rollup, po: NamedFragment): GridCellContent {
  const offering = rollup.offerings.find((o) => o.productOffering.id === po.id);
  if (!offering) return emptyCell;
  const { summary } = offering;
  if (summary)
    return { content: priceRange(summary.minCostInCents, summary.maxCostInCents), value: summary.minCostInCents };
  const { globalOptionGroup, locationName } = rollup;
  const notApplicable = offering.status === CostOptionGroupOfferingStatus.NotApplicable;
  const reason = notApplicable
    ? `${po.name} does not include location ${locationName}.`
    : `${po.name} does not offer ${globalOptionGroup.name}${locationName ? ` in location ${locationName}` : ""}.`;
  return {
    content: () => <Tooltip title={reason}>{notApplicable ? "N/A" : <Tag type="update" text="Not offered" />}</Tooltip>,
    value: notApplicable ? "N/A" : "Not offered",
  };
}

/** The amount's min to max, sorting by the min. */
function rangeCell(stats: Stats): GridCellContent {
  return { content: priceRange(stats.minInCents, stats.maxInCents), value: stats.minInCents };
}

/** "$1.00 – $2.00", or a single amount when the metric doesn't vary. */
function priceRange(minInCents: number, maxInCents: number): string {
  const min = formatToPrice({ valueInCents: minInCents });
  return minInCents === maxInCents ? min : `${min} - ${formatToPrice({ valueInCents: maxInCents })}`;
}
